import { sortBy } from "lodash";
import moment from "moment";
import pluralize from "pluralize";
import {
  ComplianceProgramStep,
  GetComplianceProgramEditQuery,
} from "types/graphql-schema";

import { ProgramEvaluationPeriodType, ProgramForm } from "./types";

export const getEvaluationPeriodType = (
  complianceProgram: GetComplianceProgramEditQuery["complianceProgram"],
): ProgramForm["evaluationPeriod"] => {
  const defaultType = {
    type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
  };
  if (!complianceProgram) {
    return defaultType;
  }

  const { periodEndDate, periodStartDate } = complianceProgram;
  if (!periodEndDate || !periodStartDate) {
    return defaultType;
  }

  const weeksDiff = moment(periodEndDate).diff(periodStartDate, "week");
  if (weeksDiff === 1) {
    return {
      type: ProgramEvaluationPeriodType.PAST_WEEK,
    };
  }
  if (weeksDiff === 2) {
    return {
      type: ProgramEvaluationPeriodType.PAST_TWO_WEEKS,
    };
  }

  const monthsDiff = moment(periodEndDate).diff(periodStartDate, "month");
  if (monthsDiff === 1) {
    return {
      type: ProgramEvaluationPeriodType.PAST_MONTH,
    };
  }
  if (monthsDiff === 2) {
    return {
      type: ProgramEvaluationPeriodType.PAST_TWO_MONTHS,
    };
  }
  if (monthsDiff === 3) {
    return {
      type: ProgramEvaluationPeriodType.PAST_QUARTER,
    };
  }
  if (monthsDiff === 6) {
    return {
      type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
    };
  }

  const yearsDiff = moment(periodEndDate).diff(periodStartDate, "year");
  if (yearsDiff === 1) {
    return {
      type: ProgramEvaluationPeriodType.PAST_YEAR,
    };
  }

  return {
    type: ProgramEvaluationPeriodType.CUSTOM,
    startDate: periodStartDate,
    endDate: periodEndDate,
  };
};

export const convertEvaluationPeriodTypeToPeriod = (
  evaluationPeriod: ProgramForm["evaluationPeriod"],
  startDate?: string | null,
): {
  periodStartDate?: string;
  periodEndDate?: string;
} => {
  if (!startDate) {
    return {};
  }

  switch (evaluationPeriod.type) {
    case ProgramEvaluationPeriodType.PAST_WEEK:
      return {
        periodStartDate: moment(startDate)
          .subtract(1, "week")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_TWO_WEEKS:
      return {
        periodStartDate: moment(startDate)
          .subtract(2, "week")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_MONTH:
      return {
        periodStartDate: moment(startDate)
          .subtract(1, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_TWO_MONTHS:
      return {
        periodStartDate: moment(startDate)
          .subtract(2, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_QUARTER:
      return {
        periodStartDate: moment(startDate)
          .subtract(3, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_HALF_YEAR:
      return {
        periodStartDate: moment(startDate)
          .subtract(6, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_YEAR:
      return {
        periodStartDate: moment(startDate)
          .subtract(1, "year")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.CUSTOM:
      return {
        periodStartDate: evaluationPeriod.startDate,
        periodEndDate: evaluationPeriod.endDate,
      };
  }
};

export const stepOrder: { [key in ComplianceProgramStep]: number } = {
  [ComplianceProgramStep.PreCalibration]: 0,
  [ComplianceProgramStep.SelfReview]: 1,
  [ComplianceProgramStep.PeerNomination]: 2,
  [ComplianceProgramStep.PeerReview]: 3,
  [ComplianceProgramStep.UpwardReview]: 4,
  [ComplianceProgramStep.DownwardReview]: 5,
  [ComplianceProgramStep.PostCalibration]: 6,
  [ComplianceProgramStep.Delivery]: 7,
  [ComplianceProgramStep.OneOnOne]: 8,
  [ComplianceProgramStep.EngagementSurvey]: 100,
};

export const getMaxStepDueDateOffset = (
  program: ProgramForm,
  step: ComplianceProgramStep,
): string | null => {
  const orderedSteps = sortBy(program.steps, (s) => stepOrder[s.stepType]);
  const stepIndex = orderedSteps.findIndex((s) => s.stepType === step);
  const nextStep =
    stepIndex < orderedSteps.length - 1
      ? orderedSteps[stepIndex + 1]
      : undefined;

  if (program.ongoing) {
    if (program.startDateOffset && program.startDateDueDateOffset) {
      const fullProgramOffset = `${program.startDateDueDateOffset - program.startDateOffset}`;
      return nextStep && "dueDateOffset" in nextStep && nextStep.dueDateOffset
        ? nextStep.dueDateOffset
        : fullProgramOffset;
    }
  } else {
    if (program.startDate && program.dueDate) {
      const fullProgramOffset = moment(program.dueDate)
        .diff(program.startDate, "days")
        .toString();
      return nextStep && "dueDateOffset" in nextStep && nextStep.dueDateOffset
        ? nextStep.dueDateOffset
        : fullProgramOffset;
    }
  }

  return null;
};

export const getMinStepDueDateOffset = (
  program: ProgramForm,
  step: ComplianceProgramStep,
): string | null => {
  const orderedSteps = sortBy(program.steps, (s) => stepOrder[s.stepType]);
  const stepIndex = orderedSteps.findIndex((s) => s.stepType === step);
  const prevStep = stepIndex > 0 ? orderedSteps[stepIndex - 1] : undefined;

  if (
    (program.ongoing &&
      program.startDateOffset &&
      program.startDateDueDateOffset) ||
    (!program.ongoing && program.startDate && program.dueDate)
  ) {
    const minOffset = "1";
    return prevStep && "dueDateOffset" in prevStep && prevStep.dueDateOffset
      ? prevStep.dueDateOffset
      : minOffset;
  }

  return null;
};

export const questionCountLabel = (questionCount: number) =>
  pluralize("question", questionCount, true);

/**
 * "6 Oct – 3 Nov": the year once at the end when both dates share it, and not at
 * all when that is this year. A range across two years says both.
 */
export const formatDateRange = (start: string, end: string) => {
  const from = moment(start);
  const to = moment(end);
  if (from.year() !== to.year()) {
    return `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;
  }
  const range = `${from.format("D MMM")} – ${to.format("D MMM")}`;
  return from.year() === moment().year() ? range : `${range} ${from.year()}`;
};
